import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Card,
  CardMedia,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { imageService } from '../../services/firebase';

interface ImageUploadProps {
  images: string[];
  onImagesChange: (images: string[]) => void;
  maxImages?: number;
  disabled?: boolean;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  images,
  onImagesChange,
  maxImages = 5,
  disabled = false
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    // Check if adding these files would exceed the limit
    if (images.length + files.length > maxImages) {
      setError(`Maximum ${maxImages} images allowed`);
      return;
    }

    uploadFiles(files);
  };

  const uploadFiles = async (files: File[]) => {
    setUploading(true);
    setError('');

    try {
      const uploadPromises = files.map(async (file) => {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          throw new Error(`${file.name} is not an image file`);
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          throw new Error(`${file.name} is too large. Maximum size is 5MB`);
        }

        // Upload to Firebase Storage
        const imageUrl = await imageService.uploadImage(file, 'products');
        return imageUrl;
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      const newImages = [...images, ...uploadedUrls];
      onImagesChange(newImages);
    } catch (error: any) {
      setError(error.message || 'Failed to upload images');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = async (index: number) => {
    try {
      const imageUrl = images[index];
      // Remove from Firebase Storage
      await imageService.deleteImage(imageUrl);
      
      // Remove from local state
      const newImages = images.filter((_, i) => i !== index);
      onImagesChange(newImages);
    } catch (error) {
      const newImages = images.filter((_, i) => i !== index);
      onImagesChange(newImages);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
        Product Images ({images.length}/{maxImages})
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {/* Existing Images */}
        {images.map((imageUrl, index) => (
          <Box key={index} sx={{ flex: '1 1 200px', minWidth: '200px', maxWidth: '250px' }}>
            <Card sx={{ position: 'relative', aspectRatio: '1/1' }}>
              <CardMedia
                component="img"
                image={imageUrl}
                alt={`Product image ${index + 1}`}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
              <IconButton
                onClick={() => handleRemoveImage(index)}
                disabled={disabled || uploading}
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  bgcolor: 'rgba(0,0,0,0.7)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.9)',
                  }
                }}
                size="small"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
              {index === 0 && (
                <Chip
                  label="Primary"
                  size="small"
                  color="primary"
                  sx={{
                    position: 'absolute',
                    bottom: 4,
                    left: 4,
                    fontSize: '0.7rem'
                  }}
                />
              )}
            </Card>
          </Box>
        ))}

        {/* Upload Button */}
        {images.length < maxImages && (
          <Box sx={{ flex: '1 1 200px', minWidth: '200px', maxWidth: '250px' }}>
            <Card
              sx={{
                aspectRatio: '1/1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: disabled || uploading ? 'not-allowed' : 'pointer',
                border: '2px dashed',
                borderColor: 'grey.300',
                bgcolor: 'grey.50',
                '&:hover': {
                  borderColor: disabled || uploading ? 'grey.300' : 'primary.main',
                  bgcolor: disabled || uploading ? 'grey.50' : 'primary.light',
                },
                transition: 'all 0.3s ease'
              }}
              onClick={handleUploadClick}
            >
              <Box sx={{ textAlign: 'center' }}>
                {uploading ? (
                  <CircularProgress size={32} />
                ) : (
                  <>
                    <AddIcon sx={{ fontSize: 32, color: 'grey.500', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Add Image
                    </Typography>
                  </>
                )}
              </Box>
            </Card>
          </Box>
        )}
      </Box>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        disabled={disabled || uploading}
      />

      {/* Upload Button (Alternative) */}
      <Box sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
          onClick={handleUploadClick}
          disabled={disabled || uploading || images.length >= maxImages}
          sx={{ textTransform: 'none' }}
        >
          {uploading ? 'Uploading...' : 'Upload Images'}
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
          Max {maxImages} images, 5MB each. Supported: JPG, PNG, WebP
        </Typography>
      </Box>

      {images.length === 0 && (
        <Box
          sx={{
            mt: 2,
            p: 3,
            border: '2px dashed',
            borderColor: 'grey.300',
            borderRadius: 2,
            textAlign: 'center',
            bgcolor: 'grey.50'
          }}
        >
          <ImageIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            No images uploaded yet. Click "Upload Images" or drag and drop images here.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ImageUpload;
